import { Box, Divider, Skeleton, Stack, Typography } from '@mui/material'
import {
    FeatureFlagIdActionTicketType,
    FeatureFlagIdCopilotInsights,
    FeatureFlagIdGrantDestinations,
    FeatureFlagIdGrantSources,
    FeatureFlagIdReviewsPendingActions,
} from 'c1gen/features'
import { C1IconButton } from 'c1ui'
import { C1DisplayNameSkeleton } from 'components/core/display-name-details'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { C1Icon } from 'components/core/icons/C1Icon'
import { Insights } from 'components/core/insights'
import { ActionsLabelHidden } from 'components/core/list/C1Table/ActionsLabelHidden'
import {
    C1TableCellProps,
    C1TableGroupHeaderRow,
    C1TableRedux,
    C1TableReduxColumn,
} from 'components/core/list/C1Table/C1TableRedux'
import List from 'components/core/list/index'
import { TaskBulkActionDropdown } from 'components/core/task/bulk-selected/TaskBulkSelectedActionDropdown'
import { ContextualTaskSearchFilter } from 'components/core/task/ContextualTaskSearchFilter'
import { CurrentState } from 'components/core/task/current-state'
import { ReviewInsights } from 'components/core/task/insights/review-insights'
import { TaskRecommendation } from 'components/core/task/recommendation/TaskRecommendation'
import { taskSearchFilters } from 'components/core/task/TaskSearchFilter'
import {
    TaskInheritedEntitlementWithFetch,
} from 'components/core/task-list-v2/task-list-v2-components/TaskInheritedEntitlementWithFetch'
import {
    TaskPrimaryActionsV2,
    TaskPrimaryActionsV2Variant,
} from 'components/core/task-list-v2/task-list-v2-components/TaskPrimaryActionsV2'
import { TaskViewLinkV2 } from 'components/core/task-list-v2/task-list-v2-components/TaskViewLinkV2'
import { DueDateString, RelativeTimestamp } from 'components/core/time/display'
import { LastLoginDisplay } from 'components/core/time/display/LastLoginDisplay'
import { VariableText } from 'components/core/variable-text'
import { useFeatureChecker } from 'components/no-ui/capability'
import { AppDisplayName } from 'components/pages/app/app-display-name'
import { C1EntitlementDisplayNameWithFetch } from 'components/pages/entitlement/display-name'
import { ResourceDisplayNameWithFetch } from 'components/pages/entitlement/resource'
import {
    ResourceTypeDisplayNameWithEntitlementFetch,
} from 'components/pages/entitlement/resource-type/ResourceTypeDisplayName'
import { ExportData } from 'components/pages/explore/query/ExportData'
import { getTaskTitleIntl } from 'components/pages/task/details/content/getTaskTitleIntl'
import { TaskKeyValueMetadataRow } from 'components/pages/task/details/content/TaskKeyValueMetadataRow'
import { TaskDrawer } from 'components/pages/task/details/drawer/TaskDrawer'
import { TaskUserDisplayName } from 'components/pages/task/details/task-content-v2/access-diagram/AccessDiagramV2'
import {
    TaskActionOptions,
    TaskActionsDropdownV2,
} from 'components/pages/task/details/task-content-v2/actions/TaskActionsDropdownV2'
import { canMakeADecisionOn, EnrollmentStatusChip, TaskStatusChip } from 'components/pages/task/state'
import {
    ActionTaskType,
    CertifyTaskType,
    GrantTaskType,
    RevokeTaskType,
    taskTypeFromString,
} from 'components/pages/task/type'
import { userStatusToIntl } from 'components/pages/user/type'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { useDrawer } from 'hooks/useDrawer'
import { TaskValue } from 'lib/actions/task'
import { useInitialFilters } from 'lib/search'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { GetPendingSummaryRequest } from 'pbts/c1/api/accessreview/v1/accessreview_pending'
import { AppEntitlementRef } from 'pbts/c1/api/app/v1/app_refs'
import { ExportDataRequest, QueryId } from 'pbts/c1/api/exportdata/v1/exportdata'
import { TaskSearchCountRequest, TaskSearchRequest } from 'pbts/c1/api/task/v1/search'
import { TaskState, TaskType } from 'pbts/c1/api/task/v1/task'
import { Timestamp } from 'pbts/google/protobuf/timestamp'
import { FC, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { getPendingSummaryApi } from 'redux_toolkit/access_review/pending_actions'
import { getAppEntitlementMultiApi } from 'redux_toolkit/entitlements'
import { usePendingTicketActionTracker } from 'redux_toolkit/lib/PendingTicketActionTracker'
import { setInvalidateAfterTimestamp } from 'redux_toolkit/slices/pendingActionsSliceV2'
import { SelectionSliceKeys, useSelections, useSelectionsState } from 'redux_toolkit/slices/selectionsSlice'
import { getTaskSearchCountApi, tasksSearchApi } from 'redux_toolkit/tasks'
import { TaskAssignedTo } from './task-list-v2-components/TaskAssignedTo'
import { TaskPrimaryActionsPendingV2 } from './task-list-v2-components/TaskPrimaryActionsPendingV2'

export enum TaskListColumnsV2 {
    viewLink,
    currentState,
    resourceType,
    entitlement,
    entitlementDescription,
    resource,
    actions,
    insights,
    recommendation,
    account,
    accountOwner,
    assignedTo,
    status,
    app,
    due,
    project,
    createdOn,
    resolvedOn,
    enrollmentStatus,
    inheritedFrom,
    department,
    jobTitle,
    createdBy,
}

export type TaskListV2Props = {
    filter?: Partial<TaskSearchRequest>
    columns?: TaskListColumnsV2[]
    displayFilters?: taskSearchFilters
    enableBulkActions?: boolean
    // Sometimes we want to enable selecting tasks for a bulk action, but put the controls elsewhere on the page.
    hideBulkActionControls?: boolean
    enableExpandTaskView?: boolean
    taskPrimaryActionsVariant?: TaskPrimaryActionsV2Variant
    allowExport?: boolean
    columnConfigurationIsActive?: boolean
    // Custom key for column configuration storage. If not provided, uses pathname.
    columnConfigurationKey?: string
    // Optional list of columns available for customization. If not provided, all columns are available.
    availableColumnsForCustomization?: TaskListColumnsV2[]
    pageSize?: number
    emptyState?: React.ReactNode
    allowSelectAllInFilter?: boolean
    taskTypeOptions?: TaskType[]
    groupBy?: (task: TaskValue) => string
    GroupLabel?: FC<{ groupKey: string; rows: TaskValue[] }>
    customizeFilters?: boolean
    hasPendingActions?: boolean
}

export type TaskListV2Extras = {
    enableExpandTaskView: boolean
    taskPrimaryActionsVariant?: TaskPrimaryActionsV2Variant
    taskSearchRequest?: TaskSearchRequest
    hasPendingActions?: boolean
}

const TaskListV2ActionsColumn: FC<C1TableCellProps<TaskValue, TaskListV2Extras>> = ({
    row,
    toggleRowIsExpanded,
    rowIsExpanded,
    extras,
}) => {
    const intl = useIntl()
    // Pending mode: feature enabled + certify task
    const isPendingMode = extras.hasPendingActions && row.type?.taskType.oneofKind === 'certify'
    const canDecide = canMakeADecisionOn(row)

    const getDropdownOptions = (): TaskActionOptions[] => {
        // Always show all regular actions regardless of pending mode
        const allRegularActions = [
            TaskActionOptions.approveWithComment,
            TaskActionOptions.denyWithComment,
            TaskActionOptions.comment,
            TaskActionOptions.reassign,
            TaskActionOptions.cancel,
            TaskActionOptions.delete,
            TaskActionOptions.restart,
            TaskActionOptions.hardReset,
            TaskActionOptions.sendReminder,
            TaskActionOptions.markProvisionComplete,
            TaskActionOptions.markProvisionErrored,
            TaskActionOptions.processNow,
            TaskActionOptions.skipStep,
        ]

        if (isPendingMode && canDecide) {
            // In pending mode when can decide, show pending-specific options plus all regular actions
            // but exclude approveWithComment and denyWithComment (use pending versions instead)
            const regularActionsWithoutApproveDeny = allRegularActions.filter(
                (action) => action !== TaskActionOptions.approveWithComment && action !== TaskActionOptions.denyWithComment,
            )
            return [
                TaskActionOptions.pendingApproveWithComment,
                TaskActionOptions.pendingDenyWithComment,
                ...regularActionsWithoutApproveDeny,
            ]
        }

        // Show all regular actions (for both pending mode when can't decide, and non-pending mode)
        return allRegularActions
    }

    const dropdownOptions = getDropdownOptions()

    return <Stack direction="row" spacing={1} alignItems="center" justifyContent="right">
        <Box>
            {
                isPendingMode
                    ? <TaskPrimaryActionsPendingV2 task={row} variant={extras.taskPrimaryActionsVariant} />
                    : <TaskPrimaryActionsV2
                        task={row}
                        variant={extras.taskPrimaryActionsVariant}
                    />
            }
        </Box>
        <Box>
            <TaskActionsDropdownV2
                task={row}
                options={dropdownOptions}
            />
        </Box>
        {extras.enableExpandTaskView && <Box>
            <C1IconButton
                onClick={(): void => toggleRowIsExpanded()}
                aria-label={rowIsExpanded
                    ? intl.formatMessage({ id: 'components_task_list_collapse_details', defaultMessage: 'Collapse task details' })
                    : intl.formatMessage({ id: 'components_task_list_expand_details', defaultMessage: 'Expand task details' })
                }
            >
                {rowIsExpanded ? <C1Icon icon={ChevronUp} /> : <C1Icon icon={ChevronDown} />}
            </C1IconButton>
        </Box>}
    </Stack>
}

export const colsMap: Record<TaskListColumnsV2, C1TableReduxColumn<TaskValue, TaskListV2Extras>> = {
    [TaskListColumnsV2.viewLink]: {
        field: 'view',
        flex: 0,
        label: <FormattedMessage id="task_table_task" defaultMessage="Task #" />,
        Component: ({ row, extras }) => <TaskViewLinkV2 task={row} hasPendingActions={extras.hasPendingActions} />,
    },
    [TaskListColumnsV2.currentState]: {
        field: 'currentState',
        label: <FormattedMessage id="task_table_current_state" defaultMessage="Current state" />,
        minWidth: '150px',
        Component: ({ row }) => <CurrentState task={row} />,
    },
    [TaskListColumnsV2.app]: {
        field: 'app',
        label: <FormattedMessage id="task_table_app" defaultMessage="Application" />,
        loadingElement: <C1DisplayNameSkeleton />,
        Component: ({ row }) => (row.app
            ? <AppDisplayName
                app={row.app}
                displayNameVariant="body1"
                readOnly
            />
            : <C1EmptyIndicator />),
    },
    [TaskListColumnsV2.resourceType]: {
        field: 'resourceType',
        label: <FormattedMessage id="task_table_resource_type" defaultMessage="Resource type" />,
        loadingElement: <C1DisplayNameSkeleton />,
        Component: ({ row }) => (row.appEntitlements
            ? <ResourceTypeDisplayNameWithEntitlementFetch
                entitlementId={row.appEntitlements[0].id}
                appId={row.appEntitlements[0].appId}
                displayNameVariant="body1"

            />
            : <C1EmptyIndicator />),
    },
    [TaskListColumnsV2.entitlement]: {
        field: 'entitlement',
        label: <FormattedMessage id="task_table_entitlement" defaultMessage="Entitlement" />,
        loadingElement: <C1DisplayNameSkeleton />,
        Component: ({ row }) => (row.appEntitlements
            ? <Box>
                <C1EntitlementDisplayNameWithFetch
                    entitlementId={row.appEntitlements[0].id}
                    appId={row.appEntitlements[0].appId}
                    readOnly
                    textTruncationWidth="32em"
                    showTooltip
                />
            </Box>
            : <C1EmptyIndicator />),
    },
    [TaskListColumnsV2.entitlementDescription]: {
        field: 'entitlementDescription',
        label: <FormattedMessage id="task_table_entitlement_description" defaultMessage="Entitlement description" />,
        loadingElement: <Skeleton variant="text" width="100px" />,
        Component: ({ row }) => (row.appEntitlements?.[0]
            ? <EntitlementDescriptionWithFetch
                entitlementId={row.appEntitlements[0].id}
                appId={row.appEntitlements[0].appId}
            />
            : <C1EmptyIndicator />),
    },
    [TaskListColumnsV2.resource]: {
        field: 'resource',
        label: <FormattedMessage id="task_table_resource_v2" defaultMessage="Resource" />,
        loadingElement: <C1DisplayNameSkeleton />,
        Component: ({ row }) => {
            const appEntitlement = row.appEntitlements?.[0]

            if (!appEntitlement?.appResourceId) {
                return <C1EmptyIndicator />
            }

            return <ResourceDisplayNameWithFetch appEntitlement={appEntitlement} />
        },
    },
    [TaskListColumnsV2.insights]: {
        field: 'insights',
        label: <FormattedMessage id="task_tablev2_insights_label" defaultMessage="Insights" />,
        shouldRender: (hasFeature): boolean => (
            hasFeature(FeatureFlagIdCopilotInsights)
            || hasFeature(FeatureFlagIdGrantDestinations)
            || hasFeature(FeatureFlagIdGrantSources)
        ) || false,
        Component: ({ row }) => <ReviewInsights row={row} />,
    },
    [TaskListColumnsV2.recommendation]: {
        field: 'recommendation',
        label: <FormattedMessage id="task_table_recommendation" defaultMessage="Recommendation" />,
        shouldRender: (hasFeature): boolean =>
            (hasFeature(FeatureFlagIdCopilotInsights) && hasFeature(FeatureFlagIdReviewsPendingActions)) || false,
        Component: ({ row }) => <TaskRecommendation task={row} />,
    },
    [TaskListColumnsV2.account]: {
        field: 'account',
        label: <FormattedMessage id="task_table_account" defaultMessage="Account" />,
        loadingElement: <C1DisplayNameSkeleton />,
        Component: ({ row }) => <TaskUserDisplayName
            margin={false}
            task={row}
            includeDetails
            showTooltip
            showUserStatus
            readOnly
        />,
    },
    [TaskListColumnsV2.accountOwner]: {
        field: 'accountOwner',
        label: <FormattedMessage id="task_table_account_owner" defaultMessage="Account owner" />,
        loadingElement: <C1DisplayNameSkeleton />,
        Component: ({ row }) => (row.identityUser
            ? <UserDisplayName
                margin={false}
                user={row.identityUser}
                showUserStatus
                readOnly
                showTooltip
            />
            : <C1EmptyIndicator />),
    },
    [TaskListColumnsV2.assignedTo]: {
        field: 'assignedTo',
        label: <FormattedMessage id="task_table_account_assigned_to" defaultMessage="Assigned to" />,
        loadingElement: <C1DisplayNameSkeleton />,
        Component: ({ row }) => <TaskAssignedTo task={row} />,
    },
    [TaskListColumnsV2.status]: {
        field: 'status',
        label: <FormattedMessage id="task_table_status" defaultMessage="Status" />,
        Component: ({ row }: { row: TaskValue }): React.ReactElement => <TaskStatusChip taskView={row} />,
    },
    [TaskListColumnsV2.project]: {
        field: 'project',
        label: <FormattedMessage id="project_name" defaultMessage="Access review" />,
        Component: ({ row }: { row: TaskValue }): React.ReactElement => (
            <Typography>{row.accessReview?.displayName}</Typography>
        ),
    },
    [TaskListColumnsV2.due]: {
        field: 'due',
        label: <FormattedMessage id="task_table_due" defaultMessage="Due" />,
        Component: ({ row }: { row: TaskValue }): React.ReactElement => (
            <Box component="span">
                {row.accessReview && row.state !== TaskState.CLOSED ? (
                    <DueDateString
                        dueDate={
                            row.accessReview.completionDate
                                ? Timestamp.toDate(row.accessReview?.completionDate)
                                : new Date()
                        }
                        prefix={false}
                    />
                ) : <C1EmptyIndicator />}
            </Box>
        ),
    },
    [TaskListColumnsV2.createdOn]: {
        field: 'createdOn',
        label: <FormattedMessage id="task_table_created_on" defaultMessage="Created on" />,
        Component: ({ row }: { row: TaskValue }): React.ReactElement => (
            <Box component="span">
                {row.createdAt ? (
                    <RelativeTimestamp
                        date={Timestamp.toDate(row.createdAt)}
                        variant="date_only"
                    />
                ) : <C1EmptyIndicator />}
            </Box>
        ),
    },
    [TaskListColumnsV2.resolvedOn]: {
        field: 'resolvedOn',
        label: <FormattedMessage id="task_table_resolved_on" defaultMessage="Resolved on" />,
        Component: ({ row }: { row: TaskValue }): React.ReactElement => {
            const taskType = row.type?.taskType
            let outcomeTime: Timestamp | undefined

            if (taskType?.oneofKind === 'grant') {
                outcomeTime = taskType.grant.outcomeTime
            } else if (taskType?.oneofKind === 'revoke') {
                outcomeTime = taskType.revoke.outcomeTime
            } else if (taskType?.oneofKind === 'certify') {
                outcomeTime = taskType.certify.outcomeTime
            } else if (taskType?.oneofKind === 'action') {
                outcomeTime = taskType.action.outcomeTime
            } else if (taskType?.oneofKind === 'offboarding') {
                outcomeTime = taskType.offboarding.outcomeTime
            }

            if (!outcomeTime || row.state !== TaskState.CLOSED) return <C1EmptyIndicator />

            return <RelativeTimestamp
                date={Timestamp.toDate(outcomeTime)}
                variant="date_only"
            />
        },
    },
    [TaskListColumnsV2.enrollmentStatus]: {
        field: 'enrollmentStatus',
        label: <FormattedMessage id="task_table_enrollment_status" defaultMessage="Enrollment status" />,
        Component: ({ row }: { row: TaskValue }): React.ReactElement => (
            <EnrollmentStatusChip taskView={row} />
        ),
    },
    [TaskListColumnsV2.inheritedFrom]: {
        field: 'inheritedFrom',
        label: <FormattedMessage id="components_app_entitlement_user_list_sources" defaultMessage="Inherited from" />,
        shouldRender: (hasFeature): boolean => hasFeature(FeatureFlagIdGrantSources) || false,
        Component: ({ row }: { row: TaskValue }): React.ReactElement => (
            <TaskInheritedEntitlementWithFetch task={row} />
        ),
    },
    [TaskListColumnsV2.department]: {
        field: 'department',
        label: <FormattedMessage id="task_table_department" defaultMessage="Subject user department" />,
        Component: ({ row }) => <Typography>{row.user?.department || <C1EmptyIndicator />}</Typography>,
    },
    [TaskListColumnsV2.jobTitle]: {
        field: 'jobTitle',
        label: <FormattedMessage id="task_table_job_title" defaultMessage="Subject user job title" />,
        Component: ({ row }) => <Typography>{row.user?.jobTitle || <C1EmptyIndicator />}</Typography>,
    },
    [TaskListColumnsV2.createdBy]: {
        field: 'createdBy',
        label: <FormattedMessage id="task_table_created_by" defaultMessage="Created by" />,
        loadingElement: <C1DisplayNameSkeleton />,
        Component: ({ row }) => (row.createdByUser
            ? <UserDisplayName user={row.createdByUser} />
            : <C1EmptyIndicator />
        ),
    },
    [TaskListColumnsV2.actions]: {
        field: 'actions',
        label: <ActionsLabelHidden />,
        isSticky: () => true,
        Component: TaskListV2ActionsColumn,
        isLocked: true,
    },
}

export const TaskListV2: FC<TaskListV2Props> = ({
    columns,
    filter: _filter,
    displayFilters,
    enableBulkActions = false,
    enableExpandTaskView = false,
    hideBulkActionControls = false,
    allowExport = false,
    columnConfigurationIsActive = false,
    columnConfigurationKey,
    availableColumnsForCustomization,
    pageSize,
    taskPrimaryActionsVariant = 'default',
    emptyState,
    allowSelectAllInFilter = true,
    taskTypeOptions,
    groupBy,
    GroupLabel,
    customizeFilters = false,
    hasPendingActions = false,
}) => {
    const intl = useIntl()
    const [page, setPage] = useState<number>(0)

    const [showDrawer, closeDrawer] = useDrawer()
    const dispatch = useDispatch()
    const pendingTicketActionTracker = usePendingTicketActionTracker()

    // Get a combined filter with the initial filter and the filter from the search bar
    const initialFilter = useInitialFilters(
        TaskSearchRequest,
        ['taskStates', 'stepApprovalTypes', 'accountTypes'],
        (k, v) => (
            k === 'taskTypes' && Array.isArray(v)
                ? v.map(taskTypeFromString)
                : v
        ),
        ['olderThanDuration', 'createdAfter', 'createdBefore'],
    )

    const [filter, setFilter] = useState<TaskSearchRequest>({ ...initialFilter, ..._filter, expandMask: { paths: ['*'] } })

    // ensures that this runs first and doesn't override any filter changes
    useLayoutEffect(() => {
        // Sync state with the prop.
        setFilter(() => ({ ...initialFilter, ..._filter, expandMask: { paths: ['*'] } }))
    }, [_filter, initialFilter])

    const isActionTicketTypeEnabled = useFeatureChecker(FeatureFlagIdActionTicketType)

    const combinedTaskSearchRequestFilters = useMemo(() => {
        const filters = {
            ...initialFilter,
            ...filter,
            expandMask: { paths: ['*'] },
        }

        if (!filters.taskTypes?.length) {
            filters.taskTypes = isActionTicketTypeEnabled
                ? [GrantTaskType, RevokeTaskType, CertifyTaskType, ActionTaskType]
                : [GrantTaskType, RevokeTaskType, CertifyTaskType]
        }

        return filters
    }, [initialFilter, filter, isActionTicketTypeEnabled])
    const combinedTaskSearchRequestFiltersDeps = JSON.stringify(combinedTaskSearchRequestFilters)

    // NOTE: avoid render loops
    const taskSearchRequest = useMemo(
        () => TaskSearchRequest.create(combinedTaskSearchRequestFilters),
        [combinedTaskSearchRequestFiltersDeps], // eslint-disable-line react-hooks/exhaustive-deps
    )
    const {
        data,
        isError,
        isLoading,
        getPage,
        canLoadMore,
        error,
        shouldInvalidateList,
        isBackgroundLoading,
    } = tasksSearchApi.useSearch(
        taskSearchRequest,
        { pageSize, notify: true },
    )

    const { data: counts } = getTaskSearchCountApi.useSearchCount(
        TaskSearchCountRequest.create({ filter: taskSearchRequest }),
    )

    const invalidateTaskCount = getTaskSearchCountApi.useInvalidateSearchCount()

    // Fetch pending actions for all visible certify tasks with an access review
    const currentPageTasks = useMemo(() => {
        if (!data || data.length === 0) return []
        return data[page] || []
    }, [data, page])

    // Get certify task IDs that have an access review (for pending actions fetch)
    const certifyTasksWithAccessReview = useMemo(() => {
        return currentPageTasks.filter(
            (task) => task.type?.taskType.oneofKind === 'certify' && task.accessReview?.id,
        )
    }, [currentPageTasks])

    // Derive accessReviewId from the first certify task (all should share the same access review)
    const accessReviewIdForPending = certifyTasksWithAccessReview[0]?.accessReview?.id
    const {
        data: pendingSummaryResponse,
    } = getPendingSummaryApi.useGetPendingSummary(
        GetPendingSummaryRequest.create({
            accessReviewId: accessReviewIdForPending ?? '',
        }),
        {
            notify: true,
            actionable: !!(hasPendingActions && accessReviewIdForPending),
        },
    )

    // Signal other components (e.g., progress bar) to refresh when task list is invalidated
    useEffect(() => {
        if (!shouldInvalidateList) return
        if (!hasPendingActions || !accessReviewIdForPending) return
        dispatch(setInvalidateAfterTimestamp(Date.now()))
    }, [shouldInvalidateList, hasPendingActions, accessReviewIdForPending, dispatch])

    // Fetch pending actions when task list changes
    useEffect(() => {
        if (!hasPendingActions || !accessReviewIdForPending || certifyTasksWithAccessReview.length === 0) return

        // Pass showLoading=false for background refreshes triggered by task list changes.
        // This prevents skeleton flashing when websocket notifications cause repeated fetches.
        // The initial loading state is set by submitReview or the slice's initialState.
        pendingTicketActionTracker?.fetchPendingActionsByTaskIds(accessReviewIdForPending, certifyTasksWithAccessReview, false)
    }, [hasPendingActions, accessReviewIdForPending, certifyTasksWithAccessReview, pendingTicketActionTracker])

    // Refetch pending actions when pending summary changes (catches pending action state changes)
    useEffect(() => {
        if (!hasPendingActions || !accessReviewIdForPending || certifyTasksWithAccessReview.length === 0) return
        if (!pendingSummaryResponse?.summary) return
        pendingTicketActionTracker?.fetchPendingActionsByTaskIds(accessReviewIdForPending, certifyTasksWithAccessReview, false)
    }, [
        pendingSummaryResponse?.summary,
        hasPendingActions,
        accessReviewIdForPending,
        certifyTasksWithAccessReview,
        pendingTicketActionTracker,
    ])

    const exportDataActionRequest = useMemo(
        () => ExportDataRequest.create({
            queryId: QueryId.TASK_SEARCH,
            request: {
                oneofKind: 'taskSearchRequest',
                taskSearchRequest,
            },
        }),
        [taskSearchRequest],
    )

    const invalidateTasksList = tasksSearchApi.useInvalidateSearch()

    const onPageChange = useCallback(
        async (n: number) => {
            await getPage(n)
            setPage(n)
        },
        [getPage],
    )

    // Handle grouping if groupBy and GroupLabel are provided
    const groupedData = useMemo(() => {
        if (!groupBy || !GroupLabel || !data) return undefined

        return data.map((pageData) => {
            const groups = new Map<string, TaskValue[]>()

            pageData.forEach((item) => {
                const groupKey = groupBy(item)
                const group = groups.get(groupKey)
                if (group) {
                    group.push(item)
                } else {
                    groups.set(groupKey, [item])
                }
            })

            return Array.from(groups.entries()).map(([groupKey, items]): C1TableGroupHeaderRow<TaskValue> => ({
                id: groupKey,
                groupKey,
                groupLabel: <GroupLabel groupKey={groupKey} rows={items} />,
                rows: items,
            }))
        })
    }, [data, groupBy, GroupLabel])

    const cols = useMemo(() => [...columns?.map((col) => colsMap[col]) || []], [columns])

    // All available columns from colsMap for column customization
    const availableCols = useMemo(() => {
        if (availableColumnsForCustomization) {
            return availableColumnsForCustomization.map((col) => colsMap[col])
        }
        return Object.values(colsMap)
    }, [availableColumnsForCustomization])

    const { actions } = useSelections<TaskValue>(SelectionSliceKeys.TASKS)

    const setSelections = useCallback((selections: TaskValue[]): void => {
        dispatch(actions.setSelections(selections))
    }, [actions, dispatch])

    const { selections } = useSelectionsState<TaskValue>(SelectionSliceKeys.TASKS)

    const [isSelectingViaFilter, setIsSelectingViaFilter] = useState(false)

    // Clear selections when the component mounts
    useEffect(() => {
        setSelections([])
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    // Clear selecting via filter when the task search request changes
    useEffect(() => {
        setIsSelectingViaFilter(false)
    }, [taskSearchRequest])

    const onReload = useCallback(() => {
        invalidateTasksList()
        invalidateTaskCount()
        setSelections([])
    }, [invalidateTasksList, invalidateTaskCount, setSelections])

    // Handle header checkbox click when in filter selection mode
    const handleHeaderCheckboxClick = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        if (isSelectingViaFilter && !event.target.checked) {
            // Clear the filter selection when unchecking
            dispatch(actions.clearSelections())
            setIsSelectingViaFilter(false)
        }
    }, [isSelectingViaFilter, actions, dispatch])

    const onTaskRowClick = useCallback(
        (_event: React.MouseEvent | React.KeyboardEvent, row: TaskValue, _extraProps: TaskListV2Extras) => {
            showDrawer(
                <TaskDrawer
                    close={closeDrawer}
                    taskId={row.id}
                    hasPendingActions={hasPendingActions}
                />,
                { variant: 'md' },
            )
        },
        [showDrawer, closeDrawer, hasPendingActions],
    )

    const staticExtraProps = useMemo((): TaskListV2Extras => ({
        enableExpandTaskView,
        taskPrimaryActionsVariant,
        taskSearchRequest: enableBulkActions ? taskSearchRequest : undefined,
        hasPendingActions,
    }), [
        enableExpandTaskView,
        taskPrimaryActionsVariant,
        enableBulkActions,
        taskSearchRequest,
        hasPendingActions,
    ])

    const getRowAriaLabel = useCallback((task: TaskValue): string => getTaskTitleIntl(task, intl), [intl])
    const table = useMemo(() => (
        <C1TableRedux<TaskValue, TaskListV2Extras>
            data={data}
            groupedData={groupedData}
            isError={isError}
            isLoading={isLoading}
            error={error}
            canLoadMore={canLoadMore}
            cols={cols}
            onPageChange={(data.length === 1 && !canLoadMore && hideBulkActionControls) ? undefined : onPageChange}
            onReload={onReload}
            emptyState={emptyState}
            onRowClick={onTaskRowClick}
            pageSize={pageSize}
            page={page}
            shouldReload={shouldInvalidateList}
            isBackgroundLoading={isBackgroundLoading}
            extraFooterActions={enableBulkActions
                ? hideBulkActionControls
                    ? <></>
                    : <TaskBulkActionDropdown
                        counts={counts}
                        taskSearchRequest={allowSelectAllInFilter ? taskSearchRequest : undefined}
                        isSelectingViaFilter={allowSelectAllInFilter ? isSelectingViaFilter : undefined}
                        setIsSelectingViaFilter={allowSelectAllInFilter ? setIsSelectingViaFilter : undefined}
                        hasPendingActions={hasPendingActions}
                    />
                : undefined}
            selections={enableBulkActions ? selections : undefined}
            setSelections={enableBulkActions ? setSelections : undefined}
            isSelectingViaFilter={enableBulkActions ? isSelectingViaFilter : undefined}
            onHeaderCheckboxClick={enableBulkActions ? handleHeaderCheckboxClick : undefined}
            getId={(task): string => task.id}
            ExpandedContent={enableExpandTaskView ? TaskListV2ExpandedContent : undefined}
            staticExtraProps={staticExtraProps}
            totalCount={counts?.count}
            columnConfigurationIsActive={columnConfigurationIsActive}
            columnConfigurationKey={columnConfigurationKey}
            availableColumns={availableCols}
            getRowAriaLabel={getRowAriaLabel}
        />
    ), [data, groupedData, isError, isLoading, error, canLoadMore,
        cols, hideBulkActionControls, onPageChange, onReload, emptyState,
        onTaskRowClick, pageSize, page, shouldInvalidateList, isBackgroundLoading,
        enableBulkActions, counts, allowSelectAllInFilter, taskSearchRequest,
        isSelectingViaFilter, selections, setSelections, handleHeaderCheckboxClick,
        enableExpandTaskView, staticExtraProps, columnConfigurationIsActive, columnConfigurationKey,
        availableCols, getRowAriaLabel, hasPendingActions])

    return <>
        <List<TaskValue>
            table={table}
            buttons={allowExport ? <ExportData exportDataRequest={exportDataActionRequest} /> : undefined}
            search={displayFilters && displayFilters.length > 0 ?
                <ContextualTaskSearchFilter
                    setFilter={setFilter}
                    filter={filter}
                    emptyFilter={_filter || {}}
                    displayFilters={displayFilters}
                    setPage={setPage}
                    taskTypeOptions={taskTypeOptions}
                    customizeFilters={customizeFilters}
                /> : undefined}
        />
    </>
}

const EntitlementDescriptionWithFetch: FC<{ entitlementId: string; appId: string }> = ({ entitlementId, appId }) => {
    const ref = useMemo(() => AppEntitlementRef.create({ appId, id: entitlementId }), [appId, entitlementId])
    const { data, isLoading } = getAppEntitlementMultiApi.useMultiGet(ref)
    if (isLoading) return <Skeleton variant="text" width="100px" />
    return <VariableText variableText={data?.description} />
}

export const TaskListV2ExpandedContent: FC<C1TableCellProps<TaskValue, TaskListV2Extras>> = ({ row, extras }) => {
    const intl = useIntl()

    return <Box sx={{ width: '100%' }}>
        <Box sx={{ padding: 4, paddingLeft: 6, backgroundColor: 'grey.50' }}>
            <Stack direction="row" gap={4}>
                <Box>
                    <Typography variant="h6"><FormattedMessage id="components_task_details_content_tab_details" defaultMessage="Details" /></Typography>
                    <Divider sx={{ my: 2 }} />

                    <Stack spacing={2}>
                        <TaskKeyValueMetadataRow
                            label={<FormattedMessage id="components_task_details_content_tab_details_email" defaultMessage="Email" />}
                            value={row.user?.email || <C1EmptyIndicator />}
                        />

                        <TaskKeyValueMetadataRow
                            label={<FormattedMessage id="components_task_details_content_tab_details_job_title" defaultMessage="Job title" />}
                            value={row.user?.jobTitle || <C1EmptyIndicator />}
                        />

                        <TaskKeyValueMetadataRow
                            label={<FormattedMessage id="components_task_details_content_tab_details_directory_status" defaultMessage="Directory status" />}
                            value={userStatusToIntl(row.user?.status, intl)}
                        />

                        <TaskKeyValueMetadataRow
                            label={<FormattedMessage id="components_task_details_content_tab_details_department" defaultMessage="Department" />}
                            value={row.user?.department || <C1EmptyIndicator />}
                        />

                        {row.appUser && <TaskKeyValueMetadataRow
                            label={<FormattedMessage id="components_task_details_content_tab_details_last_login" defaultMessage="Last login" />}
                            value={<LastLoginDisplay lastUsedAt={row.appUser.lastUsedAt} />}
                        />}

                        {row.appEntitlements?.[0] && <TaskKeyValueMetadataRow
                            align="top"
                            label={<FormattedMessage id="components_task_details_content_tab_details_entitlement_description" defaultMessage="Entitlement description" />}
                            value={<Box sx={{ maxWidth: '32em' }}>
                                <EntitlementDescriptionWithFetch
                                    entitlementId={row.appEntitlements[0].id}
                                    appId={row.appEntitlements[0].appId}
                                />
                            </Box>}
                        />}
                    </Stack>
                </Box>
                <Box>
                    <Insights insights={row.insights} task={row} displayVariant="compact" />
                </Box>

                <Box ml="auto">
                    <TaskViewLinkV2 task={row} hasPendingActions={extras.hasPendingActions} />
                </Box>
            </Stack>
        </Box>
    </Box>
}
